import * as THREE from "three";

// Define grid size constant here too for boundary checking
const GRID_SIZE = 18;

export class Monk {
  public mesh: THREE.Group;
  public color: string;
  public isStriking: boolean = false;
  private strikeAnimation: number = 0;
  private strikeDuration: number = 0.5; // seconds
  private leftArm!: THREE.Mesh;
  private rightArm!: THREE.Mesh;
  private head!: THREE.Mesh;
  private face!: THREE.Sprite;
  private isDying: boolean = false;

  constructor(color: string, position: THREE.Vector3) {
    this.color = color;
    this.mesh = this.createMonkMesh();
    this.mesh.position.copy(position);
    // Ensure rotation is reset
    this.mesh.rotation.set(0, 0, 0);
  }

  private createMonkMesh(): THREE.Group {
    const group = new THREE.Group();

    // Body (squared box instead of cylinder)
    const bodyGeometry = new THREE.BoxGeometry(0.8, 1.2, 0.6);
    // Main body color
    const bodyMaterial = new THREE.MeshPhongMaterial({ color: this.color });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.6;
    group.add(body);

    // Create robe/clothing details with slightly darker shade
    const darkerColor = new THREE.Color(this.color);
    darkerColor.multiplyScalar(0.8); // Make it 20% darker

    // Front panel of the robe (like monk's kesa)
    const panelGeometry = new THREE.BoxGeometry(0.85, 0.7, 0.05);
    const panelMaterial = new THREE.MeshPhongMaterial({ color: darkerColor });
    const panel = new THREE.Mesh(panelGeometry, panelMaterial);
    panel.position.set(0, 0.7, 0.33); // Place on front
    group.add(panel);

    // Head (sphere)
    const headGeometry = new THREE.SphereGeometry(0.35, 8, 8);
    const headMaterial = new THREE.MeshPhongMaterial({ color: "#FFE4C4" });
    this.head = new THREE.Mesh(headGeometry, headMaterial);
    this.head.position.y = 1.4;
    group.add(this.head);

    // Add hair on top (using small box)
    const hairGeometry = new THREE.BoxGeometry(0.4, 0.1, 0.4);
    const hairMaterial = new THREE.MeshPhongMaterial({ color: "#000000" });
    const hair = new THREE.Mesh(hairGeometry, hairMaterial);
    hair.position.set(0, 1.6, 0);
    group.add(hair);

    // Add emoji face using sprite
    const faceCanvas = document.createElement("canvas");
    faceCanvas.width = 64;
    faceCanvas.height = 64;
    const ctx = faceCanvas.getContext("2d");
    if (ctx) {
      ctx.font = "50px Arial";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("o", 32, 32); // Suspicious face emoji
    }

    const faceTexture = new THREE.CanvasTexture(faceCanvas);
    const faceMaterial = new THREE.SpriteMaterial({ map: faceTexture });
    this.face = new THREE.Sprite(faceMaterial);
    this.face.scale.set(0.5, 0.5, 1);
    this.face.position.set(0, 1.4, 0.4);
    group.add(this.face);

    // Feet (two boxes)
    const footGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.4);
    const footMaterial = new THREE.MeshPhongMaterial({ color: "#8B4513" });

    const leftFoot = new THREE.Mesh(footGeometry, footMaterial);
    leftFoot.position.set(-0.25, 0.1, 0);
    group.add(leftFoot);

    const rightFoot = new THREE.Mesh(footGeometry, footMaterial);
    rightFoot.position.set(0.25, 0.1, 0);
    group.add(rightFoot);

    // Arms (boxes instead of cylinders)
    const armGeometry = new THREE.BoxGeometry(0.2, 0.8, 0.2);
    const armMaterial = new THREE.MeshPhongMaterial({ color: this.color });

    // Left arm
    this.leftArm = new THREE.Mesh(armGeometry, armMaterial);
    this.leftArm.position.set(-0.5, 1.0, 0);
    this.leftArm.rotation.z = Math.PI / 4; // Angled out
    group.add(this.leftArm);

    // Right arm
    this.rightArm = new THREE.Mesh(armGeometry, armMaterial);
    this.rightArm.position.set(0.5, 1.0, 0);
    this.rightArm.rotation.z = -Math.PI / 4; // Angled out
    group.add(this.rightArm);

    return group;
  }

  public strike(): void {
    if (!this.isDying) {
      this.isStriking = true;
      this.strikeAnimation = 0;
    }
  }

  public die(): void {
    this.isDying = true;
  }

  public isDead(): boolean {
    return this.isDying;
  }

  public update(deltaTime: number): void {
    if (this.isStriking) {
      this.strikeAnimation += deltaTime;

      // Rotate the entire monk for strike animation
      const strikeRotation = Math.sin(this.strikeAnimation * Math.PI * 2) * 0.5;
      this.mesh.rotation.y = strikeRotation;

      // Animate both arms
      const armRotation =
        (Math.sin(this.strikeAnimation * Math.PI * 2) * Math.PI) / 2;
      this.leftArm.rotation.z = Math.PI / 4 + armRotation;
      this.rightArm.rotation.z = -Math.PI / 4 - armRotation;

      if (this.strikeAnimation >= this.strikeDuration) {
        this.isStriking = false;
        this.mesh.rotation.y = 0;
        this.leftArm.rotation.z = Math.PI / 4;
        this.rightArm.rotation.z = -Math.PI / 4;
      }
    }

    // If dying, update death animation
    if (this.isDying) {
      // Make monk fall backward
      this.mesh.rotation.x = Math.min(
        this.mesh.rotation.x + deltaTime * 5,
        Math.PI / 2
      );
      // Sink into the ground
      this.mesh.position.y = Math.max(
        this.mesh.position.y - deltaTime * 0.5,
        -1
      );
    }
  }

  public move(direction: THREE.Vector3): void {
    if (!this.isStriking && !this.isDying) {
      // Calculate new position
      const newPosition = this.mesh.position.clone().add(direction);

      // Get player status from custom property (will add this via Game.ts)
      const isPlayerMonk = this.mesh.userData.isPlayer === true;

      // Apply boundary constraints differently for players vs NPCs
      if (!isPlayerMonk) {
        // NPCs cannot exit the grid in any direction
        const halfSize = GRID_SIZE / 2 - 0.5;
        newPosition.x = Math.max(-halfSize, Math.min(halfSize, newPosition.x));
        newPosition.z = Math.max(-halfSize, Math.min(halfSize, newPosition.z));
      }
      // Players can exit freely in all directions

      // Apply new position
      this.mesh.position.copy(newPosition);
    }
  }
}
